import { memo, useState, useEffect, useRef } from 'react' // Importa useRef
import { motion, AnimatePresence, animate } from 'motion/react'

const positiveMessages = [
  '✨ Cargando esta nueva experiencia inmersiva...',
  '🚀 Preparando algo increíble para ti...',
  '⏳ Un momento, la magia está sucediendo...',
  '💡 Iluminando tu camino digital...',
  '🌟 Creando estrellas para tu disfrute...',
  '🎨 Pintando tu próxima aventura...'
]

/**
 * LoadingFallback component
 * @component
 * @param {object} props - The component's props.
 * @param {string} [props.initialMessage] - An optional initial message to display first.
 * @description Displays a loading message. If an initialMessage is provided,
 * it's shown first, followed by a cycle of positive messages.
 * It uses Tailwind CSS classes for styling and layout.
 * It cycles through positive messages every 2 seconds with an animation, using motion/react for timing.
 * @returns {JSX.Element} LoadingFallback component
 */
const LoadingFallback = memo(({ initialMessage }) => {
  const [frozenInitialMessage] = useState(initialMessage)
  const [initialRandomIndex] = useState(() => Math.floor(Math.random() * positiveMessages.length))
  const [displayedMessageText, setDisplayedMessageText] = useState(
    frozenInitialMessage || positiveMessages[initialRandomIndex]
  )
  const [isShowingInitialMessage, setIsShowingInitialMessage] = useState(!!frozenInitialMessage)
  const [currentPositiveIndex, setCurrentPositiveIndex] = useState(frozenInitialMessage ? 0 : initialRandomIndex)

  const animationControlsRef = useRef(null) // Para almacenar los controles de la animación
  const isActiveRef = useRef(true) // Para rastrear si el componente está activo

  useEffect(() => {
    // Al montar, asegurarse de que isActiveRef sea true
    isActiveRef.current = true

    const scheduleNextPositiveMessage = currentIndex => {
      if (!isActiveRef.current) return

      animationControlsRef.current = animate(0, 1, {
        duration: 2,
        onComplete: () => {
          if (isActiveRef.current) {
            const nextIndex = (currentIndex + 1) % positiveMessages.length
            setCurrentPositiveIndex(nextIndex)
            setDisplayedMessageText(positiveMessages[nextIndex])
            scheduleNextPositiveMessage(nextIndex)
          }
        }
      })
    }

    if (isShowingInitialMessage) {
      animationControlsRef.current = animate(0, 1, {
        duration: 2,
        onComplete: () => {
          if (isActiveRef.current) {
            setIsShowingInitialMessage(false)
            const firstPositiveCycleIndex = Math.floor(Math.random() * positiveMessages.length)
            setCurrentPositiveIndex(firstPositiveCycleIndex)
            setDisplayedMessageText(positiveMessages[firstPositiveCycleIndex])
            // El useEffect se volverá a ejecutar debido al cambio en isShowingInitialMessage
          }
        }
      })
    } else {
      setDisplayedMessageText(positiveMessages[currentPositiveIndex])
      scheduleNextPositiveMessage(currentPositiveIndex)
    }

    return () => {
      isActiveRef.current = false // Marcar como inactivo al desmontar
      if (animationControlsRef.current) {
        animationControlsRef.current.stop() // Detener la animación en curso
      }
    }
  }, [isShowingInitialMessage, frozenInitialMessage, currentPositiveIndex]) // currentPositiveIndex se añade como dependencia para reiniciar el ciclo correctamente

  const variants = {
    enter: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: 0.2
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3
      }
    }
  }

  return (
    <div
      className='flex flex-col items-center justify-center w-full h-screen text-center flex-wrap cursor-wait'
      role='status'
      aria-live='polite'
    >
      <AnimatePresence mode='wait'>
        <motion.p
          key={displayedMessageText}
          variants={variants}
          initial='exit'
          animate='enter'
          exit='exit'
          aria-label='Estado de carga'
        >
          <span className='sr-only'>Cargando:</span>
          {displayedMessageText}
        </motion.p>
      </AnimatePresence>
    </div>
  )
})

LoadingFallback.displayName = 'LoadingFallback'

export default LoadingFallback
